import { FC, useMemo, useState } from 'react';
import { Chips, ChipsProps, InputNumber, InputText, globalToastService } from '@repo/atomic-components';
import { InputNumberChangeEvent } from 'primereact/inputnumber';
import { useLazyPostAddOrganisationQuery } from '@/api';
import { GreenButton } from '@/ui/components/GreenButton';
import { OutlineButton } from '@/ui/components/OutlineButton';
import { DashboardSection } from './DashboardSection';

interface CreateOrgFormState {
  name: string;
  nameDisplayable: string;
  emailDomains: string[];
  retentionInWeeksPostCaseClosure: number | null;
  retentionInWeeksPostFirstRecordUpload: number | null;
  storageContainer: string;
}

const EMPTY_FORM: CreateOrgFormState = {
  name: '',
  nameDisplayable: '',
  emailDomains: [],
  retentionInWeeksPostCaseClosure: null,
  retentionInWeeksPostFirstRecordUpload: null,
  storageContainer: '',
};

const inputClass =
  'border-fletchers-gray-110! hover:border-fletchers-gray-110! focus:border-fletchers-gray-110! h-8! w-full text-[14px]/[20px]! focus:outline-none!';

const labelClass = 'text-fletchers-gray-200 block text-[14px]/[20px] font-[500]';

// Whole weeks, zero or more — matches DataRetentionSection's rule so an org is
// never created with a negative/fractional retention period.
const isValidWeeks = (value: number | null): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

/**
 * Azure Blob Storage container naming rules:
 * https://learn.microsoft.com/en-us/rest/api/storageservices/naming-and-referencing-containers--blobs--and-metadata
 */
const getStorageContainerError = (name: string): string | null => {
  const value = name.trim();
  if (!value) return 'Storage container name is required.';
  if (value.length < 3 || value.length > 63) {
    return 'Storage container name must be between 3 and 63 characters long.';
  }
  if (value !== value.toLowerCase()) {
    return 'Storage container name must be lowercase.';
  }
  if (!/^[a-z0-9-]+$/.test(value)) {
    return 'Storage container name can only contain lowercase letters, numbers, and hyphens.';
  }
  if (!/^[a-z0-9]/.test(value) || !/[a-z0-9]$/.test(value)) {
    return 'Storage container name must start and end with a letter or number.';
  }
  if (value.includes('--')) {
    return 'Storage container name cannot contain consecutive hyphens.';
  }
  return null;
};

/**
 * Second dashboard section — self-serve creation of a new organisation via
 * `admin-utils/add-organisation`. On success we surface the returned
 * organisationId (which the admin needs for the Azure hook-up) and reset the
 * form.
 */
interface CreateOrgSectionProps {
  className?: string;
  /** Called after a successful create so the parent can refetch the org list. */
  onCreated?: () => void;
}

export const CreateOrgSection: FC<CreateOrgSectionProps> = ({ className, onCreated }) => {
  const [form, setForm] = useState<CreateOrgFormState>(EMPTY_FORM);
  const [createdOrgId, setCreatedOrgId] = useState<string | null>(null);
  const [triggerAddOrganisation, { isFetching }] = useLazyPostAddOrganisationQuery();

  const setField = <K extends keyof CreateOrgFormState>(key: K, value: CreateOrgFormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const canSubmit = useMemo(
    () =>
      form.name.trim().length > 0 &&
      form.nameDisplayable.trim().length > 0 &&
      form.emailDomains.length > 0 &&
      form.storageContainer.trim().length > 0 &&
      isValidWeeks(form.retentionInWeeksPostCaseClosure) &&
      isValidWeeks(form.retentionInWeeksPostFirstRecordUpload),
    [form],
  );

  const handleCreate = () => {
    if (!canSubmit) return;

    const storageContainerError = getStorageContainerError(form.storageContainer);
    if (storageContainerError) {
      globalToastService.show({
        severity: 'error',
        summary: 'Invalid storage container',
        detail: storageContainerError,
      });
      return;
    }

    triggerAddOrganisation({
      name: form.name.trim(),
      nameDisplayable: form.nameDisplayable.trim(),
      emailDomains: form.emailDomains,
      retentionInWeeksPostCaseClosure: form.retentionInWeeksPostCaseClosure as number,
      retentionInWeeksPostFirstRecordUpload: form.retentionInWeeksPostFirstRecordUpload as number,
      storageContainer: form.storageContainer.trim(),
    })
      .unwrap()
      .then((response) => {
        setCreatedOrgId(response.organisationId);
        setForm(EMPTY_FORM);
        globalToastService.show({
          severity: 'success',
          summary: 'Organisation created',
          detail: `New organisation created (${response.organisationId}).`,
        });
        onCreated?.();
      })
      .catch((error) => {
        console.error('Failed to create organisation:', error);
        globalToastService.show({ severity: 'error', summary: 'Error', detail: 'Failed to create organisation.' });
      });
  };

  return (
    <DashboardSection
      title="Create new organisation"
      description="Provision a new organisation. Remember to also hook it up in Azure afterwards."
      className={className}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="org-name" className={labelClass}>
            Name
          </label>
          <InputText
            id="org-name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            placeholder="Testerini Solicitorinis of Grimsby and Cleethorpes"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="org-name-displayable" className={labelClass}>
            Display name
          </label>
          <InputText
            id="org-name-displayable"
            value={form.nameDisplayable}
            onChange={(e) => setField('nameDisplayable', e.target.value)}
            placeholder="Testerino Solicitorinis"
            className={inputClass}
          />
        </div>

        <div className="flex flex-col gap-2 md:col-span-2">
          <label htmlFor="org-email-domains" className={labelClass}>
            Email domains
          </label>
          <Chips
            id="org-email-domains"
            value={form.emailDomains}
            onChange={(e: Parameters<NonNullable<ChipsProps['onChange']>>[0]) =>
              setField('emailDomains', (e.value ?? []) as string[])
            }
            placeholder="Type a domain and press Enter (e.g. testini.co.uk)"
            className="w-full [&_.p-chips-multiple-container]:min-h-8! [&_.p-chips-multiple-container]:w-full"
            addOnBlur
            separator=","
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="org-retention-closure" className={labelClass}>
            Retention post case closure (weeks)
          </label>
          <InputNumber
            inputId="org-retention-closure"
            value={form.retentionInWeeksPostCaseClosure}
            onChange={(e: InputNumberChangeEvent) =>
              setField('retentionInWeeksPostCaseClosure', (e.value ?? null) as number | null)
            }
            min={0}
            placeholder="12"
            inputClassName={inputClass}
            className="w-full"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="org-retention-upload" className={labelClass}>
            Retention post first record upload (weeks)
          </label>
          <InputNumber
            inputId="org-retention-upload"
            value={form.retentionInWeeksPostFirstRecordUpload}
            onChange={(e: InputNumberChangeEvent) =>
              setField('retentionInWeeksPostFirstRecordUpload', (e.value ?? null) as number | null)
            }
            min={0}
            placeholder="26"
            inputClassName={inputClass}
            className="w-full"
          />
        </div>

        <div className="flex flex-col gap-2 md:col-span-2">
          <label htmlFor="org-storage-container" className={labelClass}>
            Storage container
          </label>
          <InputText
            id="org-storage-container"
            value={form.storageContainer}
            onChange={(e) => setField('storageContainer', e.target.value)}
            placeholder="testerini-solicitors"
            className={inputClass}
          />
        </div>
      </div>

      {createdOrgId && (
        <div className="border-fletchers-green-600/40 bg-fletchers-green-50 text-fletchers-green-600 flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
          <i className="pi pi-check-circle" aria-hidden />
          <span>
            Organisation created — ID: <span className="font-mono font-semibold">{createdOrgId}</span>
          </span>
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 pt-2">
        <GreenButton
          label="Create organisation"
          icon="pi pi-plus"
          disabled={!canSubmit || isFetching}
          loading={isFetching}
          onClick={handleCreate}
          style={{ height: 36 }}
        />
        <OutlineButton
          label="Reset"
          disabled={isFetching}
          onClick={() => {
            setForm(EMPTY_FORM);
            setCreatedOrgId(null);
          }}
          style={{ height: 36 }}
        />
      </div>
    </DashboardSection>
  );
};
